import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Select } from 'controlled-components'
import {
  closeTop,
  selectTopIsOpen,
  setTopSearchTerm,
} from 'slices/aiSegmentBuilder/picker'

import { MenuContainer, TriggerContainer } from './TopSelector'
import { usePickerDataFetching } from './usePickerDataFetching'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface TopSelectorContainerProps {
  pickerId?: number
  disabled?: boolean
  placeholder?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the top (single-select) dropdown.
 * Accepts pickerId and disabled as props (injected by PickerContainer via cloneElement).
 * Passes pickerId and disabled as props to all sub-containers.
 *
 * IMPORTANT: This is the ONLY place usePickerDataFetching should be called for this picker/level.
 * All child components receive the fetching functions and loading state as props.
 */
const TopSelectorContainer: React.FC<TopSelectorContainerProps> = ({
  pickerId = 0,
  disabled = false,
  placeholder,
}) => {
  const dispatch = useDispatch()

  // Read from Redux using the resolved pickerId
  const isOpen = useSelector((state: any) => selectTopIsOpen(state, pickerId))

  // Single source of truth for data fetching - all values passed to children as props
  const { search, refetch, goToPage, isLoading } = usePickerDataFetching({
    pickerId,
    level: 'top',
    debounceMs: 300,
  })

  // Handle close: reset search term
  const handleClose = useCallback(() => {
    dispatch(closeTop({ pickerId }))
    dispatch(setTopSearchTerm({ pickerId, term: '' }))
  }, [dispatch, pickerId])

  return (
    <Select isOpen={isOpen} onClose={handleClose}>
      {/* Wrapper to position menu relative to trigger */}
      <div style={{ position: 'relative' }}>
        <TriggerContainer
          pickerId={pickerId}
          placeholder={placeholder}
          disabled={disabled}
          search={search}
          refetch={refetch}
          isLoading={isLoading}
        />
        <MenuContainer
          pickerId={pickerId}
          disabled={disabled}
          isLoading={isLoading}
          goToPage={goToPage}
        />
      </div>
    </Select>
  )
}

// Add displayName for identification in PickerContainer filtering
TopSelectorContainer.displayName = 'TopSelectorContainer'

export default TopSelectorContainer
